import React, { useState, useCallback } from "react";

import AssignmentEditor from "./AssignmentEditor";
import { ViewState } from "./EditCoachingPlanHead";
import { getValueAtPath } from "utility/ObjectUtils";

interface WeeklyPlanEditorProps {
    state: ViewState;
    activityStats: any;
    selectedWeek: any;
    coachingPlan: any;
    constPlan: any;
    vetKitVersion?: string;
    // Callbacks
    setActivityCallback?: any;
    setDurationCallback?: any;
    setOptionCallback?: any;
}

const WeeklyPlanEditor = ({
    activityStats,
    coachingPlan,
    constPlan,
    selectedWeek,
    state,
    vetKitVersion,
    // Callbacks
    setActivityCallback,
    setDurationCallback,
    setOptionCallback
}: WeeklyPlanEditorProps) => {

    // ---=== useState ===--- //

    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    // ---=== useCallback ===--- //

    const refreshCallback = useCallback(() => {
        setRefreshTrigger(prev => ++prev);
    }, []);

    // ---=== Utilities ===--- //

    const hasDuration = (val) => {
        if (val && typeof (val) !== "string") {
            let duration: number = getValueAtPath(activityStats[val.Id], "Settings.Duration.Value", 120);
            return getValueAtPath(val, "Settings.Duration", duration);
        }
        return 0;
    };

    // ---=== Render ===--- //

    return (
        <div className="text-wrap w-full h- px-6 py-[15px] flex-col justify-start items-start gap-2.5 inline-flex">
            <div className="self-stretch justify-start items-center gap-6 inline-flex">
                <div className="text-gray-900 text-xl font-semibold leading-[30px]">Bonus Activities</div>
            </div>
            {
                state && (state === ViewState.Pending || state === ViewState.Active) && (
                    <div className="w-full justify-start items-start gap-6 inline-flex">
                        <div className="w-full text-wrap text-gray-900 text-sm font-normal leading-tight">Assign up to four additional activities for the patient to complete this week. Choose from Coaching, Meditation, and Movement activities.</div>
                    </div>
                )
            }
            {
                state && (state === ViewState.Cancelled) && (
                    <div className="w-full justify-start items-start gap-6 inline-flex">
                        <div className="w-full text-wrap text-gray-900 text-sm font-normal leading-tight">
                            No recorded Bonus Activity data for this week.
                        </div>
                    </div>
                )
            }
            {
                state &&
                activityStats &&
                coachingPlan?.[selectedWeek]?.assignments &&
                (state === ViewState.Pending || state === ViewState.Active || state === ViewState.Completed || state === ViewState.Interrupted || state === ViewState.CompletedPost) && (
                    <div className="w-full h-[100%] max-[1800px]:h-[100%] grid min-[1200px]:grid-rows-2 grid-cols-2 max-[1200px]:grid-cols-1 gap-4 my-2">
                        {
                            [0, 1, 2, 3].map(
                                (index: number) => (
                                    <div key={index} className="justify-center items-center inline-flex max-[1200px]:w-full min-w-0 min-h-0 relative rounded-[10px] shadow border border-gray-200">
                                        <AssignmentEditor
                                            activities={activityStats}
                                            completedRoutineData={null}
                                            currentAssignments={coachingPlan[selectedWeek].assignments}
                                            defaultAssignment={constPlan[selectedWeek].assignments[index] ?? null}
                                            defaultDuration={hasDuration(coachingPlan[selectedWeek].assignments[index])}
                                            index={index}
                                            isCalibrationActivity={false}
                                            isRoutineActivity={false}
                                            originalAssignment={constPlan[selectedWeek].assignments[index] ?? null}
                                            originalDuration={hasDuration(constPlan[selectedWeek].assignments[index])}
                                            refreshTrigger={refreshTrigger}
                                            state={state}
                                            vetKitVersion={vetKitVersion}
                                            week={selectedWeek}
                                            // Callbacks
                                            refreshParentCallback={refreshCallback}
                                            setActivityCallback={setActivityCallback}
                                            setDurationCallback={setDurationCallback}
                                            setOptionsCallback={setOptionCallback}
                                        />
                                    </div>
                                )
                            )
                        }
                    </div>
                )
            }
        </div>
    );
};

export default WeeklyPlanEditor;
